/**
 * Email Visual Builder — shared Creative Engine email mode
 *
 * Uses canonical page-builder email-mode components and output adapters.
 * Left: shared email-safe palette. Center: block editor. Preview renders the
 * canonical email HTML or AMP document generated from shared Puck content.
 */
import { useState, useCallback, useMemo, useEffect } from "react";
import {
  Type, ImageIcon, MousePointerClick, Minus, Columns2,
  Heading, Space, Plus, Trash2, Eye, Send, ChevronUp, ChevronDown,
  Save, Zap, CalendarCheck, BarChart3, Images, ChevronDown as AccordionIcon,
  Star, Settings as SettingsIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import { createEmailTemplate } from "@/hooks/use-email-templates";
import {
  EMAIL_MODE_COMPONENT_REGISTRY,
  createEmailModeComponent,
  isAmpEmailModeComponentType,
  renderEmailModeOutput,
  type EmailModeComponentType,
  type EmailModePuckComponent,
} from "@ease/page-builder";
import { EmailPreview } from "@/routes/email/campaigns/email-preview";

// ---------------------------------------------------------------------------
// Block types
// ---------------------------------------------------------------------------

const BLOCK_PALETTE: Array<{
  type: EmailModeComponentType;
  Icon: LucideIcon;
  label: string;
  isAmp: boolean;
}> = EMAIL_MODE_COMPONENT_REGISTRY.map((component) => ({
  type: component.type,
  Icon: iconForComponent(component.type),
  label: component.label,
  isAmp: isAmpEmailModeComponentType(component.type),
}));

function getBlockId(block: EmailModePuckComponent): string {
  const propsId = typeof block.props.id === "string" ? block.props.id : null;
  return block._id ?? propsId ?? "";
}

function stringProp(block: EmailModePuckComponent, key: string): string {
  const value = block.props[key];
  return typeof value === "string" ? value : "";
}

function setStringProp(
  block: EmailModePuckComponent,
  key: string,
  value: string,
): EmailModePuckComponent {
  return {
    ...block,
    props: {
      ...block.props,
      [key]: value,
    },
  };
}

function iconForComponent(type: EmailModeComponentType): LucideIcon {
  switch (type) {
    case "email_header":
      return Heading;
    case "email_text":
      return Type;
    case "email_image":
      return ImageIcon;
    case "email_button":
      return MousePointerClick;
    case "email_divider":
      return Minus;
    case "email_footer":
      return Space;
    case "email_social":
      return Columns2;
    case "email_product_card":
      return ImageIcon;
    case "amp_booking":
      return CalendarCheck;
    case "amp_poll":
      return BarChart3;
    case "amp_carousel":
      return Images;
    case "amp_accordion":
      return AccordionIcon;
    case "amp_nps":
      return Star;
    case "amp_preferences":
      return SettingsIcon;
    default:
      return Type;
  }
}

// ---------------------------------------------------------------------------
// Block renderer
// ---------------------------------------------------------------------------

function BlockPreview({ block }: { block: EmailModePuckComponent }) {
  switch (block.type) {
    case "email_header":
      return (
        <h2 className="text-xl font-bold text-[var(--color-foreground)]" style={{ textAlign: stringProp(block, "align") as "left" | "center" | "right" }}>
          {stringProp(block, "text")}
        </h2>
      );
    case "email_text":
      return (
        <p className="text-sm text-[var(--color-foreground)] leading-relaxed" style={{ textAlign: stringProp(block, "align") as "left" | "center" | "right" }}>
          {stringProp(block, "text")}
        </p>
      );
    case "email_image":
      return stringProp(block, "src") ? (
        <img src={stringProp(block, "src")} alt={stringProp(block, "alt")} className="max-w-full rounded" style={{ width: stringProp(block, "width") || "100%" }} />
      ) : (
        <div className="flex items-center justify-center h-32 bg-[var(--color-surface)] border border-dashed border-[var(--color-border)] rounded text-[var(--color-muted-foreground)] text-sm">
          <ImageIcon className="h-8 w-8 mr-2" /> Click to add image
        </div>
      );
    case "email_button":
      return (
        <div style={{ textAlign: stringProp(block, "align") as "left" | "center" | "right" }}>
          <span className="inline-block rounded-lg px-6 py-2.5 text-sm font-medium text-white" style={{ backgroundColor: stringProp(block, "backgroundColor") || "var(--color-primary)" }}>
            {stringProp(block, "text")}
          </span>
        </div>
      );
    case "email_divider":
      return <hr className="border-[var(--color-border)]" />;
    case "email_footer":
      return (
        <div className="text-center text-xs text-[var(--color-muted-foreground)]">
          <div>{stringProp(block, "companyName") || "Company name"}</div>
          <div>{stringProp(block, "address") || "Company address"}</div>
          <div className="mt-2 underline">{stringProp(block, "unsubscribeText") || "Unsubscribe"}</div>
        </div>
      );
    case "email_social":
      return (
        <div className="grid grid-cols-2 gap-4">
          <div className="text-sm text-[var(--color-foreground)]">Facebook</div>
          <div className="text-sm text-[var(--color-foreground)]">Instagram</div>
        </div>
      );
    case "email_product_card":
      return (
        <div className="rounded-lg border border-[var(--color-border)] p-4">
          <div className="text-sm font-semibold text-[var(--color-foreground)]">{stringProp(block, "name") || "Product name"}</div>
          <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">{stringProp(block, "description") || "Product description"}</div>
          <div className="mt-3 text-sm font-medium text-[var(--color-foreground)]">{stringProp(block, "price") || "$0"}</div>
        </div>
      );
    case "amp_booking":
      return <AmpPreviewCard title="AMP Booking" description={stringProp(block, "buttonText") || "Book Now"} />;
    case "amp_poll":
      return <AmpPreviewCard title="AMP Poll" description={stringProp(block, "question") || "Interactive poll"} />;
    case "amp_carousel":
      return <AmpPreviewCard title="AMP Carousel" description="Interactive image carousel" />;
    case "amp_accordion":
      return <AmpPreviewCard title="AMP Accordion" description="Expandable FAQ sections" />;
    case "amp_nps":
      return <AmpPreviewCard title="AMP NPS" description={stringProp(block, "question") || "How likely are you to recommend us?"} />;
    case "amp_preferences":
      return <AmpPreviewCard title="AMP Preferences" description="Inline preference center" />;
  }
}

function AmpPreviewCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-lg border border-[var(--color-ai)]/20 bg-[var(--color-ai)]/5 px-4 py-3">
      <div className="text-sm font-semibold text-[var(--color-foreground)]">{title}</div>
      <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{description}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Block editor (inline editing for selected block)
// ---------------------------------------------------------------------------

function BlockEditor({ block, onChange }: { block: EmailModePuckComponent; onChange: (next: EmailModePuckComponent) => void }) {
  const update = (key: string, value: string) => onChange(setStringProp(block, key, value));

  switch (block.type) {
    case "email_header":
    case "email_text":
      return (
        <div className="space-y-2">
          <textarea
            value={stringProp(block, "text")}
            onChange={(e) => update("text", e.target.value)}
            rows={block.type === "email_header" ? 2 : 4}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
          <select
            value={stringProp(block, "align") || "left"}
            onChange={(e) => update("align", e.target.value)}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-xs"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
      );
    case "email_image":
      return (
        <div className="space-y-2">
          <input
            type="url"
            value={stringProp(block, "src")}
            onChange={(e) => update("src", e.target.value)}
            placeholder="Image URL"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={stringProp(block, "alt")}
            onChange={(e) => update("alt", e.target.value)}
            placeholder="Alt text"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
          />
        </div>
      );
    case "email_button":
      return (
        <div className="space-y-2">
          <input
            type="text"
            value={stringProp(block, "text")}
            onChange={(e) => update("text", e.target.value)}
            placeholder="Button text"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
          />
          <input
            type="url"
            value={stringProp(block, "href")}
            onChange={(e) => update("href", e.target.value)}
            placeholder="Link URL"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
          />
        </div>
      );
    case "email_footer":
      return (
        <div className="space-y-2">
          <input
            type="text"
            value={stringProp(block, "companyName")}
            onChange={(e) => update("companyName", e.target.value)}
            placeholder="Company name"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={stringProp(block, "address")}
            onChange={(e) => update("address", e.target.value)}
            placeholder="Address"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
          />
        </div>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface EmbeddedEmailBuilderChange {
  ampHtml: string | null;
  blocks: EmailModePuckComponent[];
  hasAmpComponents: boolean;
  html: string;
  puckData: unknown;
}

interface EmailBuilderSurfaceProps {
  embedded?: boolean;
  initialBlocks?: EmailModePuckComponent[];
  isSaving?: boolean;
  onChange?: (payload: EmbeddedEmailBuilderChange) => void;
  onSave?: () => void | Promise<void>;
  subject?: string;
  onSubjectChange?: (value: string) => void;
}

function EmailBuilderSurface({
  embedded = false,
  initialBlocks,
  isSaving = false,
  onChange,
  onSave,
  subject,
  onSubjectChange,
}: EmailBuilderSurfaceProps) {
  const [blocks, setBlocks] = useState<EmailModePuckComponent[]>(() => initialBlocks ?? []);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState<"standard" | "amp">("standard");
  const renderedOutput = useMemo(() => renderEmailModeOutput({ content: blocks }), [blocks]);
  const hasAmpBlocks = renderedOutput.hasAmpComponents;

  const addBlock = useCallback((type: EmailModeComponentType) => {
    setBlocks((prev) => [...prev, createEmailModeComponent(type)]);
  }, []);

  const removeBlock = useCallback((id: string) => {
    setBlocks((prev) => prev.filter((block) => getBlockId(block) !== id));
    if (selectedBlockId === id) setSelectedBlockId(null);
  }, [selectedBlockId]);

  const moveBlock = useCallback((id: string, direction: -1 | 1) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((block) => getBlockId(block) === id);
      if (idx < 0) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const updated = [...prev];
      const item = updated[idx];
      const target = updated[newIdx];
      if (!item || !target) return prev;
      updated[idx] = target;
      updated[newIdx] = item;
      return updated;
    });
  }, []);

  const updateBlock = useCallback((id: string, nextBlock: EmailModePuckComponent) => {
    setBlocks((prev) => prev.map((block) => getBlockId(block) === id ? nextBlock : block));
  }, []);

  const selectedBlock = blocks.find((block) => getBlockId(block) === selectedBlockId);

  useEffect(() => {
    onChange?.({
      ampHtml: renderedOutput.ampHtml,
      blocks,
      hasAmpComponents: renderedOutput.hasAmpComponents,
      html: renderedOutput.html,
      puckData: renderedOutput.contract.puckData,
    });
  }, [blocks, onChange, renderedOutput]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          {embedded ? (
            <>
              <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Visual Builder</h3>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Build email-safe blocks here. Use HTML mode for advanced markup.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Email Builder</h1>
              <p className="text-sm text-[var(--color-muted-foreground)]">Design emails with drag-and-drop blocks.</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            <Eye className="h-4 w-4" />
            {showPreview ? "Edit" : "Preview"}
          </button>
          {hasAmpBlocks && showPreview && (
            <button
              type="button"
              onClick={() => setPreviewMode(previewMode === "standard" ? "amp" : "standard")}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                previewMode === "amp"
                  ? "border-[var(--color-ai)]/30 bg-[var(--color-ai)]/10 text-[var(--color-ai)]"
                  : "border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
              }`}
            >
              <Zap className="h-4 w-4" />
              {previewMode === "amp" ? "AMP" : "Standard"}
            </button>
          )}
          {!embedded && (
            <>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
              >
                <Send className="h-4 w-4" />
                Send Test
              </button>
              <button
                type="button"
                onClick={() => { void onSave?.(); }}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {isSaving ? "Saving..." : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      {!embedded && (
        <input
          type="text"
          value={subject ?? ""}
          onChange={(e) => onSubjectChange?.(e.target.value)}
          placeholder="Email subject line..."
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-2.5 text-sm text-[var(--color-foreground)]"
        />
      )}

      <div className="flex gap-4">
        {/* Block palette */}
        {!showPreview && (
          <div className="w-44 shrink-0 space-y-2">
            <div className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide px-1">
              Blocks
            </div>
            {BLOCK_PALETTE.filter((item) => !item.isAmp).map((item) => (
              <button
                key={item.type}
                type="button"
                onClick={() => addBlock(item.type)}
                className="flex w-full items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)] transition-colors"
              >
                <item.Icon className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                {item.label}
              </button>
            ))}

            {/* AMP interactive components section */}
            <div className="mt-4 pt-3 border-t border-[var(--color-border)]">
              <div className="text-xs font-semibold text-[var(--color-ai)] uppercase tracking-wide px-1 mb-2 flex items-center gap-1">
                <Zap className="h-3 w-3" /> AMP Interactive
              </div>
              <div className="text-[10px] text-[var(--color-muted-foreground)] px-1 mb-2">
                Gmail & Yahoo only. Others get fallback.
              </div>
              {BLOCK_PALETTE.filter((item) => item.isAmp).map((item) => (
                <button
                  key={item.type}
                  type="button"
                  onClick={() => addBlock(item.type)}
                  className="flex w-full items-center gap-2 rounded-lg border border-[var(--color-ai)]/20 px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-ai)]/5 transition-colors mb-1"
                >
                  <item.Icon className="h-4 w-4 text-[var(--color-ai)]" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Canvas */}
        <div className="flex-1 min-h-[500px]">
          <div className="mx-auto max-w-[600px] rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] shadow-sm">
            {blocks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-[var(--color-muted-foreground)]">
                <Plus className="h-8 w-8 mb-2" />
                <p className="text-sm">Add blocks from the palette to start building</p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--color-border)]">
                {blocks.map((block, idx) => (
                  <div
                    key={getBlockId(block)}
                    onClick={() => !showPreview && setSelectedBlockId(getBlockId(block))}
                    className={cn(
                      "relative px-6 py-4 transition-colors",
                      !showPreview && "cursor-pointer hover:bg-[var(--color-surface)]/50",
                      selectedBlockId === getBlockId(block) && !showPreview && "ring-2 ring-inset ring-[var(--color-primary)]/40 bg-[var(--color-primary)]/5",
                    )}
                  >
                    <BlockPreview block={block} />
                    {/* Block actions (edit mode only) */}
                    {selectedBlockId === getBlockId(block) && !showPreview && (
                      <div className="absolute top-2 right-2 flex gap-1">
                        <button type="button" onClick={(e) => { e.stopPropagation(); moveBlock(getBlockId(block), -1); }} disabled={idx === 0} className="rounded p-1 hover:bg-[var(--color-surface)] disabled:opacity-30">
                          <ChevronUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); moveBlock(getBlockId(block), 1); }} disabled={idx === blocks.length - 1} className="rounded p-1 hover:bg-[var(--color-surface)] disabled:opacity-30">
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); removeBlock(getBlockId(block)); }} className="rounded p-1 hover:bg-[var(--color-error)]/10 text-[var(--color-error)]">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Properties panel */}
        {!showPreview && selectedBlock && (
          <div className="w-64 shrink-0 space-y-3">
            <div className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide">
              {selectedBlock.type} Properties
            </div>
            <div className="rounded-lg border border-[var(--color-border)] p-3 bg-[var(--color-surface)]">
              <BlockEditor
                block={selectedBlock}
                onChange={(nextBlock) => updateBlock(getBlockId(selectedBlock), nextBlock)}
              />
            </div>
          </div>
        )}
      </div>

      {showPreview && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-3">
          <EmailPreview
            title={previewMode === "amp" && hasAmpBlocks ? "AMP email preview" : "Email preview"}
            html={previewMode === "amp" && renderedOutput.ampHtml ? renderedOutput.ampHtml : renderedOutput.html}
          />
        </div>
      )}
    </div>
  );
}

export function EmbeddedEmailBuilder({
  initialBlocks,
  onChange,
}: Pick<EmailBuilderSurfaceProps, "initialBlocks" | "onChange">) {
  return (
    <EmailBuilderSurface
      embedded
      initialBlocks={initialBlocks}
      onChange={onChange}
    />
  );
}

export default function EmailBuilderPage() {
  const [latestOutput, setLatestOutput] = useState<EmbeddedEmailBuilderChange | null>(null);
  const [subject, setSubject] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async () => {
    if (!latestOutput) {
      return;
    }

    setIsSaving(true);
    try {
      await createEmailTemplate({
        name: subject.trim() || "Saved email template",
        subject: subject.trim() || "Untitled email",
        htmlContent: latestOutput.html,
        textContent: null,
        category: "marketing",
      });
    } finally {
      setIsSaving(false);
    }
  }, [latestOutput, subject]);

  return (
    <EmailBuilderSurface
      isSaving={isSaving}
      onChange={setLatestOutput}
      onSave={handleSave}
      onSubjectChange={setSubject}
      subject={subject}
    />
  );
}
